import { CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Chip, Tooltip } from "@mui/material";
import React from "react";
import { getUserVideoRewards } from "../Api";
import VideoRewardRecordDto from "../models/dtos/VideoRewardRecordDto";

interface VideoRewardsTableProps {
    userId: number;
}

export default function VideoRewardsTable(props: VideoRewardsTableProps) {

    const [state, setState] = React.useState({
        videoRewards: [] as VideoRewardRecordDto[],
        loaded: false
    });

    const fetchData = async () => {
        let videoRewards = await getUserVideoRewards(props.userId);
        setState(prevState => ({ ...prevState, videoRewards: videoRewards ?? [], loaded: true }));
    }

    const getRewardOptionLabel = (option: string) => {
        switch (option) {
            case 'Betcoins': return 'Betcoins';
            case 'OpenBets': return 'Open Bets';
            case 'None': return 'None';
            default: return option;
        }
    }

    const getRewardOptionColor = (option: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
        switch (option) {
            case 'Betcoins': return 'success';
            case 'OpenBets': return 'primary';
            case 'None': return 'default';
            default: return 'info';
        }
    }

    const getStatusLabel = (status: VideoRewardRecordDto['status']) => status ?? 'SUCCESS';

    const getStatusColor = (status: VideoRewardRecordDto['status']): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
        switch (getStatusLabel(status)) {
            case 'FAIL': return 'error';
            case 'SUCCESS': return 'success';
            default: return 'default';
        }
    }

    React.useEffect(() => {
        fetchData();
    }, []);

    return (
        !state.loaded ? <CircularProgress size='100px' style={{ marginBottom: '32px' }} /> : <Table>
            <TableHead>
                <TableRow>
                    <TableCell align="left">ID</TableCell>
                    <TableCell align="left">User Ref</TableCell>
                    <TableCell align="left">Reward Option</TableCell>
                    <TableCell align="left">Reward Value</TableCell>
                    <TableCell align="left">Placement ID</TableCell>
                    <TableCell align="left">Referenced ID</TableCell>
                    <TableCell align="left">Referenced Value</TableCell>
                    <TableCell align="left">Status</TableCell>
                    <TableCell align="left">Error Message</TableCell>
                    <TableCell align="left">Created At</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {state.videoRewards.length === 0 ? (
                    <TableRow>
                        <TableCell colSpan={10} align="center">No video rewards found</TableCell>
                    </TableRow>
                ) : (
                    state.videoRewards.map(item => (
                        <TableRow key={item.id}>
                            <TableCell>{item.id}</TableCell>
                            <TableCell>{item.userRef}</TableCell>
                            <TableCell>
                                <Chip 
                                    label={getRewardOptionLabel(item.rewardOption)} 
                                    color={getRewardOptionColor(item.rewardOption)} 
                                    size="small" 
                                />
                            </TableCell>
                            <TableCell><b>{item.rewardValue}</b></TableCell>
                            <TableCell>{item.placementId}</TableCell>
                            <TableCell>{item.referencedId || '-'}</TableCell>
                            <TableCell>{item.referencedValue !== null ? item.referencedValue : '-'}</TableCell>
                            <TableCell>
                                <Chip
                                    label={getStatusLabel(item.status)}
                                    color={getStatusColor(item.status)}
                                    size="small"
                                />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 320 }}>
                                {item.errorMessage ? (
                                    <Tooltip
                                        title={<span style={{ whiteSpace: 'pre-wrap' }}>{item.errorMessage}</span>}
                                        componentsProps={{ tooltip: { sx: { maxWidth: 900 } } }}
                                    >
                                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {item.errorMessage}
                                        </span>
                                    </Tooltip>
                                ) : '-'}
                            </TableCell>
                            <TableCell>
                                {item.createdAt ? new Date(item.createdAt).toLocaleString() : '-'}
                            </TableCell>
                        </TableRow>
                    ))
                )}
            </TableBody>
        </Table>);
}
